import { getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Scales hero media up as the reader scrolls past it, similar in spirit to a
 * background-attachment: fixed parallax. Shared by the article hero and the
 * Metro hero slideshow so both move the same way.
 *
 * Attributes:
 * - `target`: selector for the element(s) to scale (default `img`).
 * - `fade`: optional selector for element(s) that fade out over the same scroll.
 *
 * Progress is measured from a baseline captured when this element connects
 * (or on resize/layout change): how far the element has moved from its
 * position at that moment, relative to its own height. That means scale is
 * locked at exactly 1 until the reader actually scrolls - a hero that
 * already fills most of the viewport at load doesn't start "pre-zoomed"
 * just because it occupies a lot of the scrollport.
 *
 * The target scale is recomputed from the live scroll position and the
 * displayed scale eases toward it every frame. That easing/catch-up is
 * what makes the zoom's rate of change respond to scroll speed, the way a
 * GSAP ScrollTrigger `scrub` tween does, rather than snapping straight to
 * the target value on every scroll tick.
 *
 * The rAF loop only runs while actively catching up to a scroll-driven
 * target (started on scroll/resize, stopped once settled) rather than
 * ticking forever in the background, so it costs nothing while the reader
 * is just reading and not scrolling.
 *
 * Horizon's "squeeze" desktop layout (>=990px) scrolls `.page-wrapper`
 * instead of the document (see @theme/scroll-container) - the listener has
 * to move to whichever element is actually scrolling, and follow it if the
 * viewport crosses that breakpoint.
 */
const MAX_SCALE = 1.3;
const EASE = 0.32;
const SETTLE_THRESHOLD = 0.0005;
// Faded elements are fully gone by this fraction of the zoom.
const FADE_END = 0.6;

class ScrollZoom extends HTMLElement {
  /** @type {HTMLElement[]} */
  #targets = [];
  /** @type {HTMLElement[]} */
  #fades = [];
  #targetProgress = 0;
  #currentProgress = 0;
  #rafId = null;
  #scrollTarget = null;
  #baselineTop = 0;
  #height = 1;

  #captureBaseline = () => {
    const rect = this.getBoundingClientRect();
    this.#baselineTop = rect.top;
    this.#height = rect.height || 1;
  };

  #computeTargetProgress = () => {
    const rect = this.getBoundingClientRect();
    const scrolledSinceBaseline = this.#baselineTop - rect.top;
    this.#targetProgress = Math.min(1, Math.max(0, scrolledSinceBaseline / this.#height));
  };

  #apply() {
    const scale = (1 + this.#currentProgress * (MAX_SCALE - 1)).toFixed(4);
    for (const target of this.#targets) target.style.transform = `scale(${scale})`;

    const opacity = Math.max(0, 1 - this.#currentProgress / FADE_END).toFixed(3);
    for (const fade of this.#fades) fade.style.opacity = opacity === '1.000' ? '' : opacity;
  }

  #tick = () => {
    this.#computeTargetProgress();
    this.#currentProgress += (this.#targetProgress - this.#currentProgress) * EASE;

    if (Math.abs(this.#targetProgress - this.#currentProgress) * (MAX_SCALE - 1) > SETTLE_THRESHOLD) {
      this.#rafId = requestAnimationFrame(this.#tick);
    } else {
      // Land exactly on the target so the image rests at a true scale(1), not 1.0004.
      this.#currentProgress = this.#targetProgress;
      this.#rafId = null;
    }
    this.#apply();
  };

  #onScroll = () => {
    if (this.#rafId === null) {
      this.#rafId = requestAnimationFrame(this.#tick);
    }
  };

  /**
   * The element's height/position may have changed - recapture the baseline
   * so the current scroll position becomes the new zero-zoom point.
   */
  #onResize = () => {
    this.#captureBaseline();
    this.#onScroll();
  };

  /**
   * Re-attaches the scroll listener to whichever element is the active
   * scroll container, in case the viewport just crossed the desktop/mobile
   * layout breakpoint.
   */
  #onLayoutChange = () => {
    this.#attachScrollListener();
    this.#onResize();
  };

  #attachScrollListener() {
    this.#detachScrollListener();
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  #detachScrollListener() {
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    this.#scrollTarget = null;
  }

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#targets = Array.from(this.querySelectorAll(this.getAttribute('target') || 'img'));
    const fade = this.getAttribute('fade');
    this.#fades = fade ? Array.from(this.querySelectorAll(fade)) : [];
    if (!this.#targets.length && !this.#fades.length) return;

    this.#captureBaseline();
    this.#currentProgress = 0;
    this.#apply();

    this.#attachScrollListener();
    window.addEventListener('resize', this.#onResize);
    scrollContainerMediaQuery.addEventListener('change', this.#onLayoutChange);
  }

  disconnectedCallback() {
    this.#detachScrollListener();
    window.removeEventListener('resize', this.#onResize);
    scrollContainerMediaQuery.removeEventListener('change', this.#onLayoutChange);

    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }
}

if (!customElements.get('scroll-zoom')) {
  customElements.define('scroll-zoom', ScrollZoom);
}
